import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  LawPersonControlName,
  NaturalPersonControlName,
} from '../../gk-person-search/services/law-person-form/law-person-form.model';
import { LawPersonType } from '../../gk-person-search/services/law-person/law-person.model';

type MarkAsMethods = 'markAsTouched' | 'markAsUntouched' | 'markAsPristine';

function markFormGroupsAs(
  formGroup: UntypedFormGroup | UntypedFormGroup[],
  markMethodName: MarkAsMethods,
): void {
  if (Array.isArray(formGroup)) {
    formGroup.forEach((element) => markFormGroupsAs(element, markMethodName));
  } else if (formGroup.controls) {
    const keys = Object.keys(formGroup.controls);
    keys.forEach((key) => {
      const control = formGroup.controls[key];
      if (control instanceof UntypedFormControl) {
        control[markMethodName]();
      } else if (control instanceof UntypedFormGroup) {
        markFormGroupsAs(control, markMethodName);
      }
    });
  }
}

export function markFormGroupsTouched(
  formGroup: UntypedFormGroup | UntypedFormGroup[],
): void {
  markFormGroupsAs(formGroup, 'markAsTouched');
}

export function markFormGroupsUntouched(
  formGroup: UntypedFormGroup | UntypedFormGroup[],
): void {
  markFormGroupsAs(formGroup, 'markAsUntouched');
}

export function markFormGroupsPristine(
  formGroup: UntypedFormGroup | UntypedFormGroup[],
): void {
  markFormGroupsAs(formGroup, 'markAsPristine');
}

export const getDefaultControlValue = (
  disabled?: boolean,
): { value: string; disabled?: boolean } => ({
  value: null,
  disabled: disabled,
});

export function getContactDataFormGroup(): UntypedFormGroup {
  const formBuilder = new UntypedFormBuilder();
  return formBuilder.group({
    [LawPersonControlName.Type]: LawPersonType.Natural,
    [LawPersonControlName.NaturalPersonData]: formBuilder.group({
      [NaturalPersonControlName.PhoneNumber]: [getDefaultControlValue()],
      [NaturalPersonControlName.Email]: [getDefaultControlValue()],
    }),
  });
}

export function setDisabledStatusOfControls(
  controls: UntypedFormControl[],
  disabled: boolean,
  emitEvent = false,
): void {
  controls.forEach((control) => {
    if (disabled) {
      control.disable({ emitEvent });
    } else {
      control.enable({ emitEvent });
    }
  });
}

export function createReducedFormGroup<
  T extends Partial<Record<string, FormControl | FormGroup>>,
  U extends Partial<Record<keyof T, FormControl>>,
>(
  allConfigFields: T,
  existenceParams: Partial<Record<keyof T, boolean>>,
): FormGroup<U> {
  const controlsConfig = Object.keys(allConfigFields).reduce(
    (acc, key) => {
      const typedKey = key as keyof T;
      if (existenceParams?.[typedKey]) {
        acc[typedKey] = allConfigFields[typedKey];
      }
      return acc;
    },
    {} as Record<keyof T, FormControl | FormGroup>,
  );

  return new UntypedFormBuilder().group(controlsConfig) as FormGroup<U>;
}

type ValidatorBackup = {
  validator: ValidatorFn | null;
  asyncValidator: AsyncValidatorFn | null;
  isDisabled: boolean;
};

const validatorCache = new WeakMap<AbstractControl, ValidatorBackup>();

export function disableValidators(
  control: AbstractControl | AbstractControl[],
): void {
  if (
    control instanceof FormGroup ||
    control instanceof FormArray ||
    Array.isArray(control)
  ) {
    const controls = Array.isArray(control)
      ? control
      : Object.values(control.controls);
    Object.values(controls).forEach(disableValidators);
  } else {
    if (!validatorCache.has(control)) {
      validatorCache.set(control, {
        validator: control.validator,
        asyncValidator: control.asyncValidator,
        isDisabled: control.disabled,
      });
    }

    control.clearValidators();
    control.clearAsyncValidators();
    control.updateValueAndValidity({ emitEvent: false });
  }
}

export function restoreValidators(
  control: AbstractControl | AbstractControl[],
): void {
  if (
    control instanceof FormGroup ||
    control instanceof FormArray ||
    Array.isArray(control)
  ) {
    const controls = Array.isArray(control)
      ? control
      : Object.values(control.controls);
    Object.values(controls).forEach(restoreValidators);
  } else {
    const backup = validatorCache.get(control);

    if (backup) {
      control.setValidators(backup.validator);
      control.setAsyncValidators(backup.asyncValidator);
      control.updateValueAndValidity({ emitEvent: false });

      if (backup.isDisabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }

      validatorCache.delete(control);
    }
  }
}
